import { useGlobalStore } from "../../store/useGlobalStore";
import { formatControllerData, formatNatureData, getErrorMessage } from "../../utils/helpers";
import http from "../../utils/http";
import { notify } from "../../utils/notify";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Constants
const DM_CONTROLLER_KEY = "dm_controller";
const NATURE_EXCEPTIONAL_KEY = "nature_exceptional";
const DM_CONTROLLER_SHEET_NAME = "DM_CONTROLLER";
const NATURE_EXCEPTIONAL_SHEET_NAME = "NATURE_EXCEPTIONAL";
const DOWNLOAD_MODEL_BASE_URL = "/data_model/download_model/";
const EXCEL_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const EXCEL_FILE_EXTENSION = ".xlsx";
const SUCCESS_MESSAGE_DUAL_EXPORT = "DM_CONTROLLER et NATURE_EXCEPTIONAL exportés avec succès";

export const handleDataController = async (reference: string, page: number, qs?: string) => {
  useGlobalStore.getState().setControllerLoading(true)
  try {
    let url = `/data_model/${reference}/?page=${page}`
    if (qs) url += `&${qs}`
    const res = await http.get(url);
    if (reference === "dm_controller") {
      let controllerData = formatControllerData(res.data)
      useGlobalStore.getState().setController(controllerData)
    } else if (reference === "nature_exceptional") {
      let natureData = formatNatureData(res.data)
      useGlobalStore.getState().setNature(natureData)
    } else {
      return res.data;
    }

  } catch (err) {
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  } finally {
    useGlobalStore.getState().setControllerLoading(false)
  }
}

export const handleFieldDetails = async (qs?: string) => {
  try {
    let url = `/data_model/dm_controller/`
    if (qs) url += `?${qs}`
    const res = await http.get(url);
    return res.data.results[0];
  } catch (err) {
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  }
}

export const handleFieldValues = async (key: string) => {
  try {
    let url = `/data_model/${key.toLowerCase()}/`
    const res = await http.get(url);
    let fieldToGet = key.toLowerCase().substring(3)
    return res.data.results.map((r: any) => { return { value: r[fieldToGet], label: r[fieldToGet] } })
  } catch (err) {
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  }
}

export const handleDownload = async (key?: string, filename?: string) => {
  try {
    // If key is dm_controller, download both sheets and merge
    if (key === DM_CONTROLLER_KEY) {
      const filters = useGlobalStore.getState().filters
      
      // Download DM_CONTROLLER
      let dmControllerUrl = `${DOWNLOAD_MODEL_BASE_URL}?name=${DM_CONTROLLER_KEY}`
      if (filters) dmControllerUrl += `&${filters}`
      const dmControllerResponse = await http.get(dmControllerUrl)
      
      // Download NATURE_EXCEPTIONAL
      let natureExceptionalUrl = `${DOWNLOAD_MODEL_BASE_URL}?name=${NATURE_EXCEPTIONAL_KEY}`
      const natureExceptionalResponse = await http.get(natureExceptionalUrl)
      
      // Fetch both Excel files as ArrayBuffers
      const [dmControllerBlob, natureExceptionalBlob] = await Promise.all([
        fetch(dmControllerResponse.data.url).then(res => res.arrayBuffer()),
        fetch(natureExceptionalResponse.data.url).then(res => res.arrayBuffer())
      ])
      
      // Read both workbooks
      const dmWorkbook = new ExcelJS.Workbook();
      await dmWorkbook.xlsx.load(dmControllerBlob);
      const natureWorkbook = new ExcelJS.Workbook();
      await natureWorkbook.xlsx.load(natureExceptionalBlob);

      // Create a new merged workbook
      const mergedWorkbook = new ExcelJS.Workbook();

      // Copy first sheet from each source workbook into merged workbook
      const copySheet = (src: ExcelJS.Workbook, sheetName: string) => {
        const srcWs = src.worksheets[0];
        const destWs = mergedWorkbook.addWorksheet(sheetName);
        srcWs.eachRow((row: ExcelJS.Row) => {
          destWs.addRow(row.values);
        });
      };
      copySheet(dmWorkbook, DM_CONTROLLER_SHEET_NAME);
      copySheet(natureWorkbook, NATURE_EXCEPTIONAL_SHEET_NAME);

      // Generate merged Excel file
      const mergedBuffer = await mergedWorkbook.xlsx.writeBuffer();
      const mergedBlob = new Blob([mergedBuffer], { type: EXCEL_MIME_TYPE })
      
      // Download merged file
      saveAs(mergedBlob, `${filename ?? DM_CONTROLLER_KEY}${EXCEL_FILE_EXTENSION}`)
      notify({ type: 'success', message: SUCCESS_MESSAGE_DUAL_EXPORT })
    } else {
      // Original single file download logic for other models
      let url = "/data_model/download_model/"
      let filters = useGlobalStore.getState().filters
      if (key) url += `?name=${key.toLowerCase()}`
      if (filename) url += `?file_name=${filename}`
      if (filters) url += `&${filters}`
      const response = await http.get(url)
      const link = document.createElement('a')
      link.href = response.data.url
      link.download = `${key ?? filename ?? "DM"}.xlsx`
      document.body.appendChild(link)
      link.click();
      document.body.removeChild(link);
      notify({ type: 'success', message: `${key ?? filename ?? "DM"} exporté avec succès` })
    }
  } catch (err) {
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  }
}
